// Client owner (district), #4320: the person at the district working an item,
// on every work item in all four systems. Informational only — no nudge, no
// escalation, no email ever goes to them (Analytics' cadence never reads it).
//
// Its one home is the Analytics item-metadata ledger, for every source (no ADO
// field, no Monday column, no request column: Patrick, 2026-10-12). One bulk
// read serves every row on the page; a save updates the same store, so every
// row, the to-do modal and the edit forms show one answer.
//
// "Couldn't read" is a state, never an empty map: a row whose owner store is
// unreadable says so rather than looking like nobody was named.

import { useEffect, useState } from 'react';
import { getUser } from '../auth/authService';
import { tracked, itemRef, itemResource } from './workTelemetry';
import { fetchClients } from '../Components/CS/ClientOverview/api';

const ANALYTICS = `${process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL ?? ''}`;

export const CLIENT_OWNER_LABEL = 'Client owner (district)';

/** Same canonical id the ledger keys on (item_metadata.canonical_id). */
export const ownerKey = (source, id) => `${source}:${String(id ?? '').trim().toLowerCase()}`;

let store = { status: 'idle', byKey: {}, error: null };
let pending = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(store));

async function headers() {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${user.access_token}` };
}

/** Load (once per page, or again with refresh) every item's client owner. Always resolves. */
export function loadClientOwners({ refresh = false } = {}) {
    if (pending && !refresh) return pending;
    if (store.status === 'idle' || refresh) {
        store = { ...store, status: store.status === 'ready' ? 'ready' : 'loading' };
    }
    pending = (async () => {
        const res = await fetch(`${ANALYTICS}/api/work/client-owners`, { headers: await headers() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { items = {} } = await res.json();
        const byKey = {};
        Object.entries(items).forEach(([source, ids]) => Object.entries(ids || {}).forEach(([id, name]) => {
            byKey[ownerKey(source, id)] = name;
        }));
        return byKey;
    })().then(
        (byKey) => { store = { status: 'ready', byKey, error: null }; notify(); return store; },
        (e) => {
            // Not cached: the next caller asks again.
            pending = null;
            store = { status: 'error', byKey: {}, error: e.message };
            notify();
            return store;
        },
    );
    return pending;
}

/** {status, value} for one item from a store snapshot. value is '' when none is set. */
export const ownerFor = (snapshot, item) => ({
    status: snapshot?.status || 'idle',
    value: (snapshot?.byKey || {})[ownerKey(item?.source, item?.id)] || '',
});

/** The whole store, re-rendering when it changes; loads on first use. */
export function useClientOwnerStore() {
    const [snap, setSnap] = useState(store);
    useEffect(() => {
        listeners.add(setSnap);
        if (store.status === 'idle') loadClientOwners();
        else setSnap(store);
        return () => listeners.delete(setSnap);
    }, []);
    return snap;
}

/** One item's {status, value}. */
export function useClientOwner(item) {
    return ownerFor(useClientOwnerStore(), item);
}

async function setClientOwnerUntracked(item, value) {
    const res = await fetch(
        `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/client-owner`,
        { method: 'PUT', headers: await headers(), body: JSON.stringify({ client_owner: value || '' }) },
    );
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    const out = await res.json();
    const byKey = { ...store.byKey };
    if (out.client_owner) byKey[ownerKey(item.source, item.id)] = out.client_owner;
    else delete byKey[ownerKey(item.source, item.id)];
    // A successful write is a known value for this item even if the bulk
    // read had failed; the other rows stay "couldn't read".
    store = { ...store, byKey, status: store.status === 'error' ? 'error' : 'ready' };
    notify();
    return out;
}

/** Set or clear (empty string) an item's client owner. Recorded as work_set_client_owner. */
export const setClientOwner = tracked('set_client_owner', setClientOwnerUntracked,
    ([item, value]) => ({ resource: itemResource(item), details: { ...itemRef(item), client_owner: value || '' } }));

// ---------------------------------------------------------------------------
// Picker suggestions: the client's own contacts from its success profile.
// ---------------------------------------------------------------------------

let clientsPromise = null;

/** Every client with its success profile (Client Overview's list), fetched once. Always resolves. */
export function loadCsClients() {
    if (!clientsPromise) {
        // Promise.resolve().then: a synchronous throw is a failed load, not a crash.
        clientsPromise = Promise.resolve().then(() => fetchClients())
            .then((d) => (Array.isArray(d) ? d : (d?.clients ?? [])))
            .catch((e) => { clientsPromise = null; throw e; });
    }
    return clientsPromise.catch(() => []);
}

const nameOf = (v) => (typeof v === 'string' ? v : (v?.name || '')).trim();

/**
 * Suggestions for the picker: Executive sponsor, Champions, Key contacts, in
 * that order, each named once. Anything else can be typed.
 */
export function contactsFor(client) {
    if (!client) return [];
    const out = [];
    const seen = new Set();
    const add = (name, hint) => {
        const n = nameOf(name);
        if (!n || seen.has(n.toLowerCase())) return;
        seen.add(n.toLowerCase());
        out.push({ name: n, hint });
    };
    add(client.executiveSponsor, 'Executive sponsor');
    (client.champions || []).forEach((c) => add(c, 'Champion'));
    (client.keyContacts || []).forEach((c) => add(c, ['Key contact', c?.title].filter(Boolean).join(' · ')));
    return out;
}

/** The suggestions for one client id ([] while loading or when there is no single client). */
export function useClientContacts(clientId) {
    const [contacts, setContacts] = useState([]);
    useEffect(() => {
        let alive = true;
        if (!clientId) { setContacts([]); return undefined; }
        loadCsClients().then((clients) => {
            const want = String(clientId).toLowerCase();
            const c = clients.find((x) => String(x.clientId || '').toLowerCase() === want);
            if (alive) setContacts(contactsFor(c));
        });
        return () => { alive = false; };
    }, [clientId]);
    return contacts;
}

/** Test hook. */
export function _resetClientOwners() {
    store = { status: 'idle', byKey: {}, error: null };
    pending = null;
    clientsPromise = null;
    listeners.clear();
}
